/**
 * Booking targets.
 *
 * Every booking CTA on the site used to point at one URL: Fresha's
 * `all-offer` catalogue screen. That screen makes the visitor re-pick the
 * location (two venues are listed) and re-pick the treatment they were
 * already reading about, so two of six checkout steps existed only because
 * the site threw away the context it had.
 *
 * The default target is the booking screen pinned to the Christchurch venue,
 * so location scoping costs the visitor nothing.
 *
 * Service-level deep links are NOT wired up: whether the Fresha plan
 * supports them is unconfirmed (fix plan P0-3, blocking input on the owner).
 * `BOOKING_BY_ROUTE` is the seam for them, keyed by route path.
 */

const FRESHA_ACCOUNT = "silk-clinical-aesthetics-d1yzxk56";
const FRESHA_PID = "2669966";

/**
 * Fresha's full catalogue entry point, location-agnostic. Fallback only:
 * it is the screen that costs the visitor the two extra steps.
 */
export const BOOKING_GENERIC =
  `https://www.fresha.com/book-now/${FRESHA_ACCOUNT}/all-offer?share=true&pId=${FRESHA_PID}`;

/**
 * Christchurch venue, 66 Victoria Street. The sitewide default.
 *
 * `id=2760031` pins the booking screen to the Christchurch location (verified
 * 2026-09-29), so the visitor does not re-pick the venue. The UTM set was
 * supplied by the owner (2026-09-29) so Fresha attributes website bookings.
 * The venue profile link (reviews, schema `sameAs`) stays separate:
 * `FRESHA_PROFILE_URL` in `constants.ts`.
 */
export const BOOKING_CHRISTCHURCH =
  `https://www.fresha.com/book-now/${FRESHA_ACCOUNT}/all-offer?id=2760031&share=true&pId=${FRESHA_PID}` +
  "&utm_source=silkclinical&utm_medium=owned&utm_campaign=website_booking";

/**
 * Auckland venue, 562 Richmond Road, Grey Lynn. The monthly pop-up clinic.
 *
 * Resolved 2026-08-04: the Fresha "Silk Clinical Aesthetics Auckland" venue is
 * the pop-up, run out of a room rented at the same Grey Lynn address every
 * month. It is the same business, not a second one, which is why the schema
 * links it back to the Christchurch node as `parentOrganization`.
 */
export const BOOKING_AUCKLAND =
  "https://www.fresha.com/a/silk-clinical-aesthetics-auckland-auckland-562-richmond-road-xo8hiibr";

/**
 * Per-route overrides. Extend as service-level targets are confirmed.
 *
 * `/auckland` previously fell back to the generic catalogue because the
 * relationship between the Fresha Auckland venue and the pop-up was unresolved.
 * It is now scoped to the Auckland venue, which is what that page is about.
 */
export const BOOKING_BY_ROUTE: Record<string, string> = {
  "/auckland": BOOKING_AUCKLAND,
};

/** Most specific known Fresha target for a route. */
export function bookingUrlFor(pathname: string): string {
  return BOOKING_BY_ROUTE[pathname] ?? BOOKING_CHRISTCHURCH;
}
